import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, ArrowRight, HelpCircle } from "lucide-react";
import GamesPageShell from "../../components/games/GamesPageShell";
import GameResultCard from "../../components/games/GameResultCard";
import { QUIZ_QUESTIONS } from "../../content/games";
import { trackGameEvent } from "../../lib/gameEvents";

// Fully client-side and stateless: every question and its correct answer
// ships to the browser up front. Unlike Academy's server-stateful safety
// quizzes (which withhold the answer key across 3 network calls to protect
// a scored rating), there's nothing here worth protecting: it's a short,
// factual, ungated trivia round. See Games Expansion V1 audit.
export default function QuickQuizGame() {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [answersLog, setAnswersLog] = useState([]);
  const [runId, setRunId] = useState(0);

  useEffect(() => { trackGameEvent("game_opened", { game: "quick-quiz" }); }, [runId]);

  const q = QUIZ_QUESTIONS[current];
  const isLast = current + 1 >= QUIZ_QUESTIONS.length;
  const progressPct = Math.round(((current + (answered ? 1 : 0)) / QUIZ_QUESTIONS.length) * 100);
  const score = useMemo(() => answersLog.filter((a) => a.correct).length, [answersLog]);

  function submitAnswer() {
    if (selected === null) return;
    const correct = selected === q.correctIndex;
    setAnswersLog((log) => [...log, { question: q, pickedIndex: selected, correct }]);
    setAnswered(true);
  }

  function nextQuestion() {
    if (isLast) {
      trackGameEvent("quiz_score", {
        game: "quick-quiz",
        score,
        total: QUIZ_QUESTIONS.length,
      });
      trackGameEvent("game_completed", { game: "quick-quiz" });
      setCurrent(current + 1); // moves past the last index -> finished view
      return;
    }
    setCurrent((c) => c + 1);
    setSelected(null);
    setAnswered(false);
  }

  function replay() {
    setCurrent(0);
    setSelected(null);
    setAnswered(false);
    setAnswersLog([]);
    setRunId((n) => n + 1);
  }

  const finished = current >= QUIZ_QUESTIONS.length;

  return (
    <GamesPageShell
      title="Quick Quiz"
      description="8 short, practical questions about confirmations, pickup and return checks, and spotting a risky listing."
    >
      {finished ? (
        <FinishedView score={score} total={QUIZ_QUESTIONS.length} answersLog={answersLog} onReplay={replay} />
      ) : (
        <div className="space-y-5" data-testid="quiz-playing">
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span data-testid="quiz-progress-text">Question {current + 1} of {QUIZ_QUESTIONS.length}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="font-display text-lg font-semibold" data-testid="quiz-question-text">{q.question}</div>
            <div className="mt-4 space-y-2" role="group" aria-label={q.question}>
              {q.options.map((opt, idx) => {
                const isPicked = selected === idx;
                const isCorrect = answered && idx === q.correctIndex;
                const isWrongPick = answered && idx === selected && selected !== q.correctIndex;
                let cls = "border-slate-200 hover:border-emerald-300";
                if (answered) {
                  if (isCorrect) cls = "border-emerald-400 bg-emerald-50";
                  else if (isWrongPick) cls = "border-rose-400 bg-rose-50";
                  else cls = "border-slate-200 opacity-70";
                } else if (isPicked) {
                  cls = "border-emerald-500 bg-emerald-50";
                }
                return (
                  <button
                    key={idx}
                    type="button"
                    data-testid={`quiz-option-${idx}`}
                    disabled={answered}
                    onClick={() => setSelected(idx)}
                    aria-pressed={isPicked}
                    className={`min-h-11 w-full text-left px-4 py-3 rounded-xl border-2 transition flex items-start gap-3 ${cls}`}
                  >
                    <span className="mt-0.5 w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-semibold flex-shrink-0" aria-hidden="true">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-sm text-slate-800 leading-relaxed">{opt}</span>
                    {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto flex-shrink-0" aria-label="Correct answer" />}
                    {isWrongPick && <XCircle className="w-5 h-5 text-rose-600 ml-auto flex-shrink-0" aria-label="Your answer" />}
                  </button>
                );
              })}
            </div>

            {answered && (
              <div
                className={`mt-4 rounded-xl p-4 ${selected === q.correctIndex ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}
                data-testid="quiz-explanation"
                role="status"
              >
                <div className={`flex items-center gap-2 text-sm font-semibold ${selected === q.correctIndex ? "text-emerald-800" : "text-rose-800"}`}>
                  {selected === q.correctIndex ? <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> : <XCircle className="w-4 h-4" aria-hidden="true" />}
                  {selected === q.correctIndex ? "Correct." : "Not quite."}
                </div>
                <div className="text-sm text-slate-700 mt-2 leading-relaxed">{q.explanation}</div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {!answered ? (
                <button
                  type="button"
                  data-testid="quiz-submit-answer"
                  disabled={selected === null}
                  onClick={submitAnswer}
                  className="min-h-11 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027] disabled:opacity-60"
                >
                  Submit answer
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="quiz-next-question"
                  onClick={nextQuestion}
                  className="min-h-11 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1B4332] text-white font-medium text-sm hover:bg-[#143027]"
                >
                  {isLast ? "See my results" : "Next question"} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </GamesPageShell>
  );
}

function FinishedView({ score, total, answersLog, onReplay }) {
  const pct = Math.round((score / total) * 100);
  const tone = pct >= 80 ? "emerald" : pct >= 50 ? "sky" : "amber";
  return (
    <GameResultCard
      icon={HelpCircle}
      tone={tone}
      eyebrow="Quiz complete"
      headline={`${score} of ${total} correct`}
      scoreText={`${pct}%`}
      subtext="Read below for the reasoning behind each answer."
      onReplay={onReplay}
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="quiz-review-section">
        <div className="font-display text-lg font-semibold">Review your answers</div>
        <div className="mt-3 space-y-3">
          {answersLog.map((a, idx) => (
            <div key={idx} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start gap-2">
                {a.correct
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" aria-label="Correct" />
                  : <XCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" aria-label="Incorrect" />}
                <div className="text-sm text-slate-800 font-medium flex-1">{a.question.question}</div>
              </div>
              <div className="text-xs text-slate-600 mt-2 leading-relaxed pl-6">{a.question.explanation}</div>
            </div>
          ))}
        </div>
      </div>
    </GameResultCard>
  );
}
